import React from "react";
import { Link, useParams } from "react-router-dom";
import { Box, Button, Divider } from "@mui/material";

import { EntityDetailContext } from "contexts/EntityDetailContext";

import { BaseTabs } from "components/Shared/BaseTabs";
import { EntityBrandingTab } from "./components/EntityBrandingTab";
import { EntityAdditionalInfo } from "./components/EntityAdditionalInfoTab";
import { EntityAPISetupTab } from "./components/EntityAPISetupTab";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import EntityBasicDetail from "./components/EntityBasicDetail";
import EntityPlan from "./components/EntityPlan";
import EntityUsers from "./components/EntityUsers";

import { useDetailEntityQuery } from "store/services/entityApi";

import { useAppMeta, useAuth } from "hooks";
import ServiceChargeTab from "./components/ServiceCharge/ServiceChargeTab";
import DocumentationFeeTab from "./components/DocumentationFee/DocumentationFeeTab";
import EntityEBillProvider from "../EBillProvider/EBillProviderComponents/EntityEBillProvider";

const EntityDetailScreen = () => {
  const { id } = useParams();

  const { isAccountAdmin, isSystemAdmin } = useAuth();

  const breadcrumbs = [
    {
      title: "Entities",
      path: "/app/crm/entity/list",
    },
    {
      title: "Entity Detail",
    },
  ];

  const tabs = [
    {
      label: "Users",
      value: "users",
      component: <EntityUsers entityId={id} />,
    },
    { label: "Plan", value: "plan", component: <EntityPlan /> },
    {
      label: "Branding",
      value: "branding",
      component: <EntityBrandingTab />,
    },
    {
      label: "Additional Info",
      value: "additional-info",
      component: <EntityAdditionalInfo />,
    },
    {
      label: "API Metric",
      value: "api-metric",
      component: <EntityAPISetupTab />,
    },
    ...(isAccountAdmin || isSystemAdmin
      ? [
          {
            label: "Service Charge",
            value: "service-charge",
            component: <ServiceChargeTab />,
          },
          {
            label: "Documentation Fee",
            value: "documentation-fee",
            component: <DocumentationFeeTab />,
          },
        ]
      : []),
    {
      label: "E-Bill Provider",
      value: "e-bill-provider",
      component: <EntityEBillProvider />,
    },
  ];

  //   RTK Query
  const { data: entityDetail, isLoading, error } = useDetailEntityQuery(id);

  useAppMeta(
    !entityDetail ? "Entity Detail" : `Entity - ${entityDetail?.name}`
  );

  return (
    <EntityDetailContext.Provider
      value={{ entityDetail, loadingEntity: isLoading }}
    >
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={isLoading}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />

        <BreadcrumbLayout.Paper>
          <EntityBasicDetail />

          <Divider />

          <BaseTabs tabs={tabs} loading={isLoading} />
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </EntityDetailContext.Provider>
  );
};

export default EntityDetailScreen;

const PageActions = () => {
  const { id } = useParams();

  return (
    <Box display="flex" gap={2}>
      <Link to={`/app/crm/entity/${id}/edit-plan`}>
        <Button variant="outlined">Edit Plan</Button>
      </Link>

      <Link to={`/app/crm/entity/${id}/edit`}>
        <Button variant="contained">Edit Entity</Button>
      </Link>
    </Box>
  );
};
